import { apiClient } from '@/services/apiClient'
import type {
  Building,
  BuildingSearchFilters,
  BuildingSearchRequest,
  BuildingSearchResponse,
} from '../types/buildingFilters.types'

// Field sets for performance optimization
const TABLE_FIELDS = [
  'id',
  'building_id',
  'egid',
  'city_name',
  'municipality_name',
  'district_name',
  'region_name',
  'address_full',
  'address_number',
  'area',
  'building_year',
  'floor_nb',
  'unit_nb',
  'height',
  'height_estimate',
  'category',
  'type',
  'status',
  'classification',
  'popety_classification',
  'geo_center',
  'create_timestamp',
  'update_timestamp',
]

const DETAIL_FIELDS = [
  ...TABLE_FIELDS,
  'building_volume',
  'heating_type',
  'energy_efficiency',
  'solar_potential',
  'view_quality',
  'noise_level',
  'transport_accessibility',
  'geo_polygon_wkt',
  'geo_polygon',
  'geo_portal_link',
  'dwellings',
  'lands_transactions',
  'lands_building_permits',
  'listings',
]

// Convert frontend filters to backend format
const convertFiltersToBackend = (filters: BuildingSearchFilters): any => {
  const backendFilters: any = {}

  // Location filters - new format
  if (filters.location_search) {
    backendFilters.location_search = filters.location_search
  }
  if (filters.location_type) {
    backendFilters.location_type = filters.location_type
  }

  // Direct location fields - backend expects these field names
  if (filters.municipality) backendFilters.municipality = filters.municipality
  if (filters.district) backendFilters.district = filters.district
  if (filters.region) backendFilters.region = filters.region
  if (filters.npa) backendFilters.npa = filters.npa

  // Legacy text search fields - keep for backward compatibility
  if (filters.city_name) backendFilters.city_name = filters.city_name
  if (filters.municipality_name)
    backendFilters.municipality = filters.municipality_name
  if (filters.district_name) backendFilters.district = filters.district_name
  if (filters.region_name) backendFilters.region = filters.region_name
  if (filters.address_full) backendFilters.address_full = filters.address_full
  if (filters.building_id) backendFilters.building_id = filters.building_id
  if (filters.egid) backendFilters.egid = filters.egid

  // Array fields (location IDs and new multi-select fields)
  if (filters.city_id?.length) backendFilters.city_id = filters.city_id
  if (filters.municipality_id?.length)
    backendFilters.municipality_id = filters.municipality_id
  if (filters.district_id?.length)
    backendFilters.district_id = filters.district_id
  if (filters.region_id?.length) backendFilters.region_id = filters.region_id

  // Tags filter
  if (filters.tags?.length) backendFilters.tags = filters.tags

  // Array fields for multi-select dropdowns (new filters)
  if (filters.category?.length) backendFilters.category = filters.category
  if (filters.type?.length) backendFilters.type = filters.type
  if (filters.status) backendFilters.status = filters.status
  if (filters.classification?.length)
    backendFilters.classification = filters.classification
  if (filters.popety_classification?.length)
    backendFilters.popety_classification = filters.popety_classification
  if (filters.building_period?.length)
    backendFilters.building_period = filters.building_period
  if (filters.renovation_period?.length)
    backendFilters.renovation_period = filters.renovation_period
  if (filters.building_permits_classification?.length)
    backendFilters.building_permits_classification =
      filters.building_permits_classification

  // Energy filters
  if (filters.heater_generator?.length)
    backendFilters.heater_generator = filters.heater_generator
  if (filters.heater_water_generator?.length)
    backendFilters.heater_water_generator = filters.heater_water_generator
  if (filters.power_source_heater?.length)
    backendFilters.power_source_heater = filters.power_source_heater
  if (filters.power_source_water_heater?.length)
    backendFilters.power_source_water_heater = filters.power_source_water_heater
  if (filters.minergie?.length) backendFilters.minergie = filters.minergie

  // Environmental filters
  if (filters.noise_exposure?.length)
    backendFilters.noise_exposure = filters.noise_exposure
  if (filters.travel_time_to_centers?.length)
    backendFilters.travel_time_to_centers = filters.travel_time_to_centers
  if (filters.slope?.length) backendFilters.slope = filters.slope
  if (filters.tax_burden?.length) backendFilters.tax_burden = filters.tax_burden
  if (filters.distance_to_highvoltage_powerlines?.length)
    backendFilters.distance_to_highvoltage_powerlines =
      filters.distance_to_highvoltage_powerlines
  if (filters.mountain_view?.length)
    backendFilters.mountain_view = filters.mountain_view
  if (filters.lake_view?.length) backendFilters.lake_view = filters.lake_view
  if (filters.public_transport_quality?.length)
    backendFilters.public_transport_quality = filters.public_transport_quality

  // Range fields - pass as objects with min/max properties
  if (filters.area) {
    backendFilters.area = filters.area
  }
  if (filters.building_year) {
    backendFilters.building_year = filters.building_year
  }
  if (filters.floor_nb) {
    backendFilters.floor_nb = filters.floor_nb
  }
  if (filters.unit_nb) {
    backendFilters.unit_nb = filters.unit_nb
  }
  if (filters.height) {
    backendFilters.height = filters.height
  }
  if (filters.building_volume) {
    backendFilters.building_volume = filters.building_volume
  }

  // Additional range filters from new implementation
  if (filters.renovation_year) {
    backendFilters.renovation_year = filters.renovation_year
  }
  if (filters.gross_floor_area) {
    backendFilters.gross_floor_area = filters.gross_floor_area
  }
  if (filters.protected_rank) {
    backendFilters.protected_rank = filters.protected_rank
  }
  if (filters.idc) {
    backendFilters.idc = filters.idc
  }
  // Status filters from listing repository
  if (filters.active !== undefined) backendFilters.active = filters.active
  if (filters.new_construction !== undefined)
    backendFilters.new_construction = filters.new_construction
  if (filters.is_furnished !== undefined)
    backendFilters.is_furnished = filters.is_furnished
  if (filters.pets_allowed !== undefined)
    backendFilters.pets_allowed = filters.pets_allowed
  if (filters.wheelchair_accessible !== undefined)
    backendFilters.wheelchair_accessible = filters.wheelchair_accessible
  if (filters.exact_location !== undefined)
    backendFilters.exact_location = filters.exact_location

  // Date filters from listing repository
  if (filters.active_listing_timestamp) {
    if (filters.active_listing_timestamp.min !== undefined)
      backendFilters.active_listing_timestamp_min =
        filters.active_listing_timestamp.min
    if (filters.active_listing_timestamp.max !== undefined)
      backendFilters.active_listing_timestamp_max =
        filters.active_listing_timestamp.max
  }
  if (filters.listing_timestamp) {
    if (filters.listing_timestamp.min !== undefined)
      backendFilters.listing_timestamp_min = filters.listing_timestamp.min
    if (filters.listing_timestamp.max !== undefined)
      backendFilters.listing_timestamp_max = filters.listing_timestamp.max
  }
  if (filters.construction_year) {
    if (filters.construction_year.min !== undefined)
      backendFilters.construction_year_min = filters.construction_year.min
    if (filters.construction_year.max !== undefined)
      backendFilters.construction_year_max = filters.construction_year.max
  }
  if (filters.listing_duration) {
    if (filters.listing_duration.min !== undefined)
      backendFilters.listing_duration_min = filters.listing_duration.min
    if (filters.listing_duration.max !== undefined)
      backendFilters.listing_duration_max = filters.listing_duration.max
  }
  if (filters.inactive_status_timestamp) {
    if (filters.inactive_status_timestamp.min !== undefined)
      backendFilters.inactive_status_timestamp_min =
        filters.inactive_status_timestamp.min
    if (filters.inactive_status_timestamp.max !== undefined)
      backendFilters.inactive_status_timestamp_max =
        filters.inactive_status_timestamp.max
  }

  // User filters
  if (filters.building_ids?.length)
    backendFilters.building_ids = filters.building_ids
  // Solar and energy detailed ranges
  if (filters.solar_area) {
    backendFilters.solar_area = filters.solar_area
  }
  if (filters.solar_roof_pitch) {
    backendFilters.solar_roof_pitch = filters.solar_roof_pitch
  }
  if (filters.solar_global_exposure) {
    backendFilters.solar_global_exposure = filters.solar_global_exposure
  }
  if (filters.solar_average_exposure) {
    backendFilters.solar_average_exposure = filters.solar_average_exposure
  }
  if (filters.solar_electrical_efficiency) {
    backendFilters.solar_electrical_efficiency =
      filters.solar_electrical_efficiency
  }
  if (filters.solar_thermal_efficiency) {
    backendFilters.solar_thermal_efficiency = filters.solar_thermal_efficiency
  }
  if (filters.solar_installed_power) {
    backendFilters.solar_installed_power = filters.solar_installed_power
  }
  if (filters.solar_installed_first_date) {
    backendFilters.solar_installed_first_date =
      filters.solar_installed_first_date
  }
  // Ownership filters
  if (filters.owners) {
    backendFilters.owners = filters.owners
  }
  if (filters.owners_type) {
    backendFilters.owners_type = filters.owners_type
  }

  // Land filters
  if (filters.land_area) {
    backendFilters.land_area = filters.land_area
  }

  // History filters - pass as objects with min/max properties
  if (filters.lands_transaction_number) {
    backendFilters.lands_transaction_number = filters.lands_transaction_number
  }
  if (filters.lands_transactions_date) {
    backendFilters.lands_transactions_date = filters.lands_transactions_date
  }
  if (filters.lands_transactions_price) {
    backendFilters.lands_transactions_price = filters.lands_transactions_price
  }
  if (filters.lands_building_permit_number) {
    backendFilters.lands_building_permit_number =
      filters.lands_building_permit_number
  }
  if (filters.lands_building_permits_date) {
    backendFilters.lands_building_permits_date =
      filters.lands_building_permits_date
  }
  if (filters.listing_rent_number) {
    backendFilters.listing_rent_number = filters.listing_rent_number
  }
  if (filters.listing_sale_number) {
    backendFilters.listing_sale_number = filters.listing_sale_number
  }
  if (filters.listings_listing_date) {
    backendFilters.listings_listing_date = filters.listings_listing_date
  }
  if (filters.listings_rent_date) {
    backendFilters.listings_rent_date = filters.listings_rent_date
  }
  if (filters.listings_sale_date) {
    backendFilters.listings_sale_date = filters.listings_sale_date
  }
  if (filters.listings_price) {
    backendFilters.listings_price = filters.listings_price
  }
  if (filters.listings_price_per_square_meter) {
    backendFilters.listings_price_per_square_meter =
      filters.listings_price_per_square_meter
  }

  // Boolean filters
  if (filters.has_civil_protection_shelter !== undefined)
    backendFilters.has_civil_protection_shelter =
      filters.has_civil_protection_shelter

  // Geospatial filters
  if (filters.geo_bounding_box) {
    backendFilters.bounds = filters.geo_bounding_box
  }
  if (filters.geo_polygon) {
    backendFilters.polygon = filters.geo_polygon.points
  }

  // Isochrone filter
  if (filters.isochroneOpts) {
    backendFilters.isochroneOpts = filters.isochroneOpts
  }

  return backendFilters
}

export const buildingSearchService = {
  // Optimized for table display - minimal fields
  getBuildingsForTable: async (
    request: BuildingSearchRequest
  ): Promise<BuildingSearchResponse> => {
    const backendFilters = convertFiltersToBackend(request.filters || {})

    const response = await apiClient.post('/buildings/search', {
      filters: backendFilters,
      fields: TABLE_FIELDS,
      limit: request.limit || 20,
      offset: request.offset || 0,
      sort: request.sort || [{ field: 'create_timestamp', order: 'desc' }],
    })

    return response.data
  },

  // Optimized for detail view - all fields
  getBuildingForDetail: async (id: string): Promise<Building> => {
    const fieldsParam = encodeURIComponent(DETAIL_FIELDS.join(','))
    const response = await apiClient.get(
      `/buildings/${id}?fields=${fieldsParam}`
    )

    return response.data
  },

  // Generic search with custom fields
  searchBuildings: async (
    request: BuildingSearchRequest
  ): Promise<BuildingSearchResponse> => {
    const backendFilters = convertFiltersToBackend(request.filters || {})

    const response = await apiClient.post('/buildings/search', {
      filters: backendFilters,
      fields: request.fields || TABLE_FIELDS,
      limit: request.limit || 20,
      offset: request.offset || 0,
      sort: request.sort || [{ field: 'create_timestamp', order: 'desc' }],
    })

    return response.data
  },

  // Search buildings within map bounds
  searchBuildingsByBounds: async (bounds: any): Promise<Building[]> => {
    const response = await apiClient.post('/buildings/search', {
      filters: { bounds },
      fields: ['id', 'geo_center', 'geo_polygon', 'category', 'type', 'status'],
      limit: 1000, // Large limit for map display
    })

    return response.data.buildings
  },
}
